import {
    TRegionSEO,
    TRegionAPI,
    TRegionISO,
    TLocaleAPI,
    TLanguage,
    TLocaleSEO,
    TLocale,
    TCurrency,
    TLocaleISO,
} from '@com/core/common/types/locale';
import { reportEvent } from './sentry';
import { setCookie } from './client';

import { TMarketplace, TMarketplaceConfig } from '../types/config';

import { MARKETPLACE_CONFIG, getMarketplaceConfig } from './config';

type TRegionMinimal = { api: TRegionAPI; seo: TRegionSEO };
type TSubpathVariation = { [TKey in TLocaleISO]: TLocaleAPI };

export default class Locale {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static locales: Record<TLocaleISO, TLocale> | any = {};

    static marketplaceConfig: TMarketplaceConfig = MARKETPLACE_CONFIG;

    public static isInitialized(reqLocale: TLocaleISO): boolean {
        return !!this.locales[reqLocale];
    }

    public static initialize(reqLocale: TLocaleISO, marketplace?: TMarketplace): void {
        let lang: TLanguage = 'en';
        let isoRegion: TRegionISO = 'AE';

        if (!reqLocale) {
            const errorMessage = '☢️ Locale initialization failed! reqLocale not found';
            reportEvent(errorMessage, 'Locale initialize', 'critical', { reqLocale });
            throw new Error(errorMessage);
        }

        try {
            [lang, isoRegion] = reqLocale.split('-') as [TLanguage, TRegionISO];
            if (marketplace) {
                this.marketplaceConfig = getMarketplaceConfig(marketplace);
            }
        } catch (error) {
            reportEvent('Invalid locale in url query', 'Locale.initialize()', 'critical', { reqLocale, error });
            throw new Error('☢️ Locale initialization failed! reqLocale invalid');
        }

        // if the locale is already initialized, return
        if (this.locales && this.locales[reqLocale]) {
            return;
        }

        // Convert SEO regions to API
        let seoRegion: TRegionSEO = 'uae';
        switch (isoRegion) {
            case 'EG':
                seoRegion = 'egypt';
                break;
            case 'SA':
                seoRegion = 'saudi';
                break;
            case 'AE':
                seoRegion = 'uae';
                break;
            default:
                reportEvent('Could not determine region', 'Locale initialize', 'warning', { isoRegion });
        }

        const apiRegion = isoRegion.toLowerCase() as TRegionAPI;

        // Finally, set the locale object to use everywhere
        this.locales[reqLocale] = {
            lang,
            seoRegion,
            apiRegion,
            seoLocale: `${seoRegion}-${lang}` as TLocaleSEO,
            apiLocale: `${lang}-${apiRegion}` as TLocaleAPI,
            isoLocale: `${lang}-${isoRegion}` as TLocaleISO,
            isEnglish: lang === 'en',
            isArabic: lang === 'ar',
            isUAE: isoRegion === 'AE',
            isSaudi: isoRegion === 'SA',
            isEgypt: isoRegion === 'EG',
            currencyCode: this.marketplaceConfig.regions[apiRegion].currencies[lang] as TCurrency,
            currencyCodeEN: this.marketplaceConfig.regions[apiRegion].currencies.en as TCurrency,
        };
    }

    public static getLocaleObject(reqLocale: TLocaleISO, marketplace?: TMarketplace): TLocale {
        this.initialize(reqLocale, marketplace);
        return this.locales[reqLocale];
    }

    public static setLocaleObject(reqLocale: TLocaleISO, localeObj: TLocale): void {
        this.locales[reqLocale] = localeObj;
    }

    public static getAPIRegion(reqLocale: TLocaleISO): TRegionAPI {
        this.initialize(reqLocale);
        const { apiRegion } = this.locales[reqLocale];
        if (!apiRegion) {
            reportEvent('Failed to get API region', 'getAPIRegion()');
        }
        return apiRegion;
    }

    public static getSEORegion(reqLocale: TLocaleISO): TRegionSEO {
        this.initialize(reqLocale);
        const { seoRegion } = this.locales[reqLocale];
        if (!seoRegion) {
            reportEvent('Failed to get SEO region', 'getSEORegion()');
        }
        return seoRegion;
    }

    public static getAPILocale(reqLocale: TLocaleISO): TLocaleAPI {
        this.initialize(reqLocale);
        const { apiLocale } = this.locales[reqLocale];
        if (!apiLocale) {
            reportEvent('Failed to get API locale', 'getAPILocale()');
        }
        return apiLocale;
    }

    public static getSEOLocale(reqLocale: TLocaleISO): TLocaleSEO {
        this.initialize(reqLocale);
        const { seoLocale } = this.locales[reqLocale];
        if (!seoLocale) {
            reportEvent('Failed to get SEO locale', 'getSEOLocale()');
        }

        return seoLocale;
    }

    public static getCurrencyCode(reqLocale: TLocaleISO): TCurrency {
        this.initialize(reqLocale);
        const { currencyCode } = this.locales[reqLocale];
        if (!currencyCode) {
            reportEvent('Failed to get Currency Code', 'getCurrencyCode()');
        }

        return currencyCode;
    }

    public static getCurrencyCodeEN(reqLocale: TLocaleISO): TCurrency {
        this.initialize(reqLocale);
        const { currencyCodeEN } = this.locales[reqLocale];
        if (!currencyCodeEN) {
            reportEvent('Failed to get Currency Code EN', 'getCurrencyCodeEn()');
        }

        return currencyCodeEN;
    }

    public static getISOLocale(reqLocale: TLocaleISO): TLocaleISO {
        this.initialize(reqLocale);
        const { isoLocale } = this.locales[reqLocale];
        // localStorage.setItem('isoLocale', isoLocale);
        if (!isoLocale) {
            reportEvent('Failed to get ISO locale', 'getISOLocale()');
        }
        return isoLocale;
    }

    public static isArabic(reqLocale: TLocaleISO): boolean {
        this.initialize(reqLocale);
        return this.locales[reqLocale].isArabic;
    }

    public static isEgypt(reqLocale: TLocaleISO): boolean {
        this.initialize(reqLocale);
        return this.locales[reqLocale].isEgypt;
    }

    public static getLang(reqLocale: TLocaleISO): TLanguage {
        this.initialize(reqLocale);
        return this.locales[reqLocale].lang;
    }

    // --- Get all regions
    // #TODO: Get rid of the any return type
    public static getRegions(): TRegionMinimal[] {
        const regions = Object.keys(this.marketplaceConfig.regions).map((regionKey: string) => {
            if (!this.marketplaceConfig.regions[regionKey].enabled) {
                return null;
            }

            return {
                api: regionKey as TRegionAPI,
                seo: this.marketplaceConfig.regions[regionKey].seo,
            };
        });

        return regions.filter((x: TRegionMinimal | null) => x !== null) as TRegionMinimal[];
    }

    // --- For i18n
    public static getSubpathVariations(): TSubpathVariation[] {
        const variations = ([] as TSubpathVariation[]).concat(
            ...this.marketplaceConfig.languages.map((language: TLanguage) => {
                return this.getRegions().map((region: TRegionMinimal) => {
                    return {
                        [`${language}-${region.api.toUpperCase()}`]: `${region.seo}-${language}`,
                    } as TSubpathVariation;
                });
            }),
        );

        return variations;
    }

    public static getCountryName(region: TRegionSEO): string {
        const country = region === 'saudi' ? 'sa' : region;

        return country;
    }

    public static async setCountry(reqLocale: TLocaleISO, region: TRegionSEO): Promise<void> {
        this.initialize(reqLocale);
        const locale = this.locales[reqLocale];

        const country = this.getCountryName(region);
        const seoLocale = `${region}-${locale.lang}`;
        const apiLocale = `${locale.lang}-${country}`;

        setCookie('nloc', apiLocale);

        setTimeout(() => {
            window.location.href = `${window.location.origin}/${seoLocale}`;
        }, 300);
    }

    public static async setLanguage(reqLocale: TLocaleISO, language: TLanguage): Promise<void> {
        this.initialize(reqLocale);
        const locale = this.locales[reqLocale];
        const seoLocale = `${locale.seoRegion}-${language}`;

        const apiLocale = `${language}-${locale.apiRegion}`;

        setCookie('nloc', apiLocale);

        setTimeout(() => {
            window.location.href = window.location.href.replace(
                new RegExp(/\/(uae|saudi|egypt)-(en|ar)/),
                `/${seoLocale}`,
            );
        }, 300);
    }
}
